import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { FileUploader } from "@/shared/components";
import { useAppDispatch, useAppSelector, useFormDraft } from "@/shared/hooks";
import { Button, Checkbox, CitySelect, DatePicker, Input, Select, Toast } from "@/shared/ui";
import { formatDateToISO, getFamilyStatusOptions, type Locale } from "@/shared/utils";
import { CITIZENSHIP_OPTIONS, getNationalityOptions, setGender, setIsResident, setIsStudent } from "@/features/hr/hiring";
import { hasAnyValue } from "../partialSave";
import { useCreatePersonalInfoMutation, useGetPersonalInfoQuery } from "./api";
import type { PersonalInfoDto } from "./types";
import { type PersonalInfoFormInput, type PersonalInfoFormValues, createPersonalInfoSchema } from "./validation";
import GeneralFormSkeleton from "./GeneralFormSkeleton";

interface Props {
  token: string;
  onNext: () => void;
  isChief?: boolean;
}

export default function GeneralForm({ token, onNext, isChief }: Props) {
  const { i18n, t } = useTranslation("ApplyApplicationPage");
  const { t: tCommon } = useTranslation("Common");
  const locale = (i18n.language as Locale) || "ru";
  const dispatch = useAppDispatch();
  const isStudentFromRedux = useAppSelector((s) => s.completeness.is_student);
  const { data, isLoading } = useGetPersonalInfoQuery(token);
  const [createPersonalInfo, { isLoading: isSaving }] = useCreatePersonalInfoMutation();
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [toast, setToast] = useState<{
    text: string;
    color: "positive" | "negative" | "notice" | "grey";
  } | null>(null);

  const [isPhotoRemoved, setIsPhotoRemoved] = useState(false);
  const [isEnrollmentRemoved, setIsEnrollmentRemoved] = useState(false);

  const isStudent = isStudentFromRedux ?? false;
  const hasPhotoFromApi = !!data?.photo_file_url && !isPhotoRemoved;
  const hasEnrollmentFromApi = !!data?.enrollment_verification_file_url && !isEnrollmentRemoved;

  const schema = useMemo(
    () =>
      createPersonalInfoSchema(isStudent, {
        hasPhotoFromApi,
        hasEnrollmentFromApi,
      }),
    [isStudent, hasPhotoFromApi, hasEnrollmentFromApi],
  );

  const methods = useForm<PersonalInfoFormInput, any, PersonalInfoFormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      iin: "",
      name: "",
      surname: "",
      father_name: "",
      date_of_birth: null,
      gender: "",
      family_status: "",
      city_of_birth_id: undefined as number | undefined,
      city_of_birth_other: "",
      nationality: "",
      citizenship: "",
      is_resident: true,
      is_student: false,
      enrollment_start_date: null,
      enrollment_end_date: null,
      photo_file: null,
      enrollment_verification_file: null,
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors, submitCount },
  } = methods;

  const { applyDraft, hasValidDraft, clearDraft } = useFormDraft<PersonalInfoFormInput, any, PersonalInfoFormValues>({
    key: `onboarding:general:${token}`,
    form: methods,
    schemaVersion: "v1",
    dateFields: ["date_of_birth", "enrollment_start_date", "enrollment_end_date"],
    ignoreFields: ["photo_file", "enrollment_verification_file"],
  });

  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (data && !dataLoadedRef.current) {
      dataLoadedRef.current = true;
      reset({
        iin: data.iin ?? "",
        name: data.name ?? "",
        surname: data.surname ?? "",
        father_name: data.father_name ?? "",
        date_of_birth: data.date_of_birth ? new Date(data.date_of_birth) : null,
        gender: data.gender ?? "",
        family_status: data.family_status ?? "",
        city_of_birth_id: data.city_of_birth_id ?? undefined,
        city_of_birth_other: data.city_of_birth_other ?? "",
        nationality: data.nationality ?? "",
        citizenship: data.citizenship ?? "",
        is_resident: data.is_resident ?? true,
        is_student: data.is_student ?? false,
        enrollment_start_date: data.enrollment_start_date ?? null,
        enrollment_end_date: data.enrollment_end_date ?? null,
        photo_file: null,
        enrollment_verification_file: null,
      });
      setIsPhotoRemoved(false);
      setIsEnrollmentRemoved(false);
      if (data.gender) {
        dispatch(setGender(data.gender as "male" | "female"));
      }
      dispatch(setIsResident(data.is_resident ?? true));
      dispatch(setIsStudent(data.is_student ?? false));
      if (hasValidDraft) applyDraft();
    } else if (!data && hasValidDraft) {
      applyDraft();
    }
  }, [data, reset, dispatch, hasValidDraft, applyDraft]);

  useEffect(() => {
    if (isLoading || !data) return;

    if (submitCount > 0) {
      trigger(["photo_file", "enrollment_verification_file"]);
    }
  }, [hasPhotoFromApi, hasEnrollmentFromApi, trigger, isLoading, data, submitCount]);

  const iinValue = watch("iin");

  useEffect(() => {
    if (!iinValue || iinValue.length < 7 || !/^\d{7}/.test(iinValue)) return;

    const yy = parseInt(iinValue.substring(0, 2), 10);
    const mm = parseInt(iinValue.substring(2, 4), 10);
    const dd = parseInt(iinValue.substring(4, 6), 10);
    const centuryDigit = parseInt(iinValue[6], 10);

    let century: number;
    if (centuryDigit === 1 || centuryDigit === 2) century = 1800;
    else if (centuryDigit === 3 || centuryDigit === 4) century = 1900;
    else if (centuryDigit === 5 || centuryDigit === 6) century = 2000;
    else return;

    const year = century + yy;
    const date = new Date(year, mm - 1, dd);

    if (date.getFullYear() === year && date.getMonth() === mm - 1 && date.getDate() === dd) {
      setValue("date_of_birth", date, { shouldValidate: true });
    }

    const parsedGender = centuryDigit % 2 === 1 ? "male" : "female";
    setValue("gender", parsedGender, { shouldValidate: true });
    dispatch(setGender(parsedGender));
  }, [iinValue, setValue, dispatch]);

  const dateValue = watch("date_of_birth");
  const gender = watch("gender");
  const familyStatus = watch("family_status");
  const cityOfBirthId = watch("city_of_birth_id");
  const cityOfBirthOther = watch("city_of_birth_other");
  const nationality = watch("nationality");
  const citizenship = watch("citizenship");
  const isResident = watch("is_resident");
  const isStudentValue = watch("is_student");
  const enrollmentStartDate = watch("enrollment_start_date");
  const enrollmentEndDate = watch("enrollment_end_date");
  const photo = watch("photo_file");
  const enrollmentFile = watch("enrollment_verification_file");

  async function handleSave() {
    if (isSaving) return;
    const values = getValues();

    const partial: Partial<PersonalInfoDto> = {};

    if (values.iin) partial.iin = values.iin;
    if (values.name) partial.name = values.name;
    if (values.surname) partial.surname = values.surname;
    if (values.father_name) partial.father_name = values.father_name;
    if (values.date_of_birth) partial.date_of_birth = formatDateToISO(values.date_of_birth);
    if (values.gender) partial.gender = values.gender as "male" | "female";
    if (values.family_status) partial.family_status = values.family_status as PersonalInfoDto["family_status"];

    const hasCityId = values.city_of_birth_id != null;
    const hasCityOther = values.city_of_birth_other?.trim();
    if (hasCityId) {
      partial.city_of_birth_id = values.city_of_birth_id;
      partial.city_of_birth_other = null;
    } else if (hasCityOther) {
      partial.city_of_birth_id = null;
      partial.city_of_birth_other = hasCityOther;
    }

    if (values.nationality) partial.nationality = values.nationality;
    if (values.citizenship) partial.citizenship = values.citizenship;

    partial.is_resident = values.is_resident;
    partial.is_student = values.is_student;
    if (values.is_student) {
      if (values.enrollment_start_date) partial.enrollment_start_date = values.enrollment_start_date;
      if (values.enrollment_end_date) partial.enrollment_end_date = values.enrollment_end_date;
    }

    const photoFromApi = !!data?.photo_file_url && !isPhotoRemoved;
    const enrollmentFromApi = !!data?.enrollment_verification_file_url && !isEnrollmentRemoved;
    if (!photoFromApi && values.photo_file) partial.photo_file = values.photo_file;
    if (!enrollmentFromApi && values.enrollment_verification_file) {
      partial.enrollment_verification_file = values.enrollment_verification_file;
    }

    if (!hasAnyValue(partial)) return;

    try {
      setIsSavingDraft(true);
      await createPersonalInfo({ token, body: partial }).unwrap();
      setToast({ color: "positive", text: "personal.toast.success" });
    } catch {
      setToast({ color: "negative", text: "personal.toast.error" });
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function onSubmit(values: PersonalInfoFormValues) {
    if (isSaving) return;

    try {
      const hasPhotoFromApi = !!data?.photo_file_url && !isPhotoRemoved;
      const hasEnrollmentFromApi = !!data?.enrollment_verification_file_url && !isEnrollmentRemoved;

      const body = {
        ...values,
        photo_file: hasPhotoFromApi ? null : (values.photo_file ?? null),
        date_of_birth: formatDateToISO(values.date_of_birth),
        enrollment_verification_file: hasEnrollmentFromApi ? null : (values.enrollment_verification_file ?? null),
        enrollment_start_date: values.is_student ? (values.enrollment_start_date ?? null) : null,
        enrollment_end_date: values.is_student ? (values.enrollment_end_date ?? null) : null,
        city_of_birth_id: values.city_of_birth_id ?? null,
        city_of_birth_other: values.city_of_birth_other?.trim() || null,
      };

      await createPersonalInfo({
        token,
        body,
      }).unwrap();

      clearDraft();
      setToast({
        color: "positive",
        text: "personal.toast.success",
      });
      onNext();
    } catch {
      setToast({
        color: "negative",
        text: "personal.toast.error",
      });
    }
  }

  if (isLoading && !data) return <GeneralFormSkeleton />;

  return (
    <form className="flex flex-col justify-between min-h-[640px] gap-7" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-7">
        <h2 className="text-display-2xs content-base-primary">{t("personal.title")}</h2>

        <div className="flex flex-col gap-4">
          <FileUploader
            label={t("personal.photo.label")}
            hasRequiredLabel
            accept=".jpg,.jpeg,.png"
            maxSizeMB={50}
            formatsText={t("file_uploader.formats_images_only", { max: 50 })}
            value={photo}
            existingFileUrl={!isPhotoRemoved ? data?.photo_file_url : null}
            onChange={(file) => {
              setValue("photo_file", file, { shouldValidate: true });
              if (file) {
                setIsPhotoRemoved(false);
              } else {
                setIsPhotoRemoved(false);
                trigger("photo_file");
              }
            }}
            onRemoveExisting={() => {
              setIsPhotoRemoved(true);
              setValue("photo_file", null, { shouldValidate: true });
            }}
            error={errors.photo_file?.message ? t(errors.photo_file.message as string) : null}
          />

          <Input
            label={t("personal.surname.label")}
            hasRequiredLabel
            placeholder={t("personal.surname.placeholder")}
            {...register("surname")}
            disabled={isChief && !!data?.surname}
            error={errors.surname?.message ? t(errors.surname.message) : undefined}
          />

          <Input
            label={t("personal.name.label")}
            hasRequiredLabel
            placeholder={t("personal.name.placeholder")}
            {...register("name")}
            disabled={isChief && !!data?.name}
            error={errors.name?.message ? t(errors.name.message) : undefined}
          />

          <Input
            label={t("personal.father_name.label")}
            placeholder={t("personal.father_name.placeholder")}
            {...register("father_name")}
            disabled={isChief && !!data?.father_name}
            error={errors.father_name?.message ? t(errors.father_name.message) : undefined}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label={t("personal.iin.label")}
              hasRequiredLabel
              placeholder={t("personal.iin.placeholder")}
              {...register("iin")}
              disabled={isChief && !!data?.iin}
              error={errors.iin?.message ? t(errors.iin.message) : undefined}
            />

            <div className="flex flex-col gap-2">
              <label className="text-sm text-black dark:text-white leading-5 font-semibold">
                {t("personal.date_of_birth.label")}
              </label>

              <DatePicker
                mode="single"
                locale={locale}
                placeholder={t("personal.date_of_birth.placeholder")}
                value={dateValue}
                disabled={isChief && !!data?.iin}
                onChange={(v) => setValue("date_of_birth", (v as Date) ?? null, { shouldValidate: true })}
              />

              {errors.date_of_birth && (
                <p className="text-body-regular-xs content-action-negative">{t(errors.date_of_birth.message!)}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm text-black dark:text-white leading-5 font-semibold">
              {t("personal.gender.label")}
              <span className="content-action-negative ml-1">*</span>
            </label>

            <Select
              options={[
                { label: t("personal.gender.male"), value: "male" },
                { label: t("personal.gender.female"), value: "female" },
              ]}
              value={gender}
              disabled={isChief && !!data?.iin}
              onChange={(v) => {
                setValue("gender", v ?? "", { shouldValidate: true });
                dispatch(setGender((v as "male" | "female") ?? null));
              }}
              placeholder={t("personal.gender.placeholder")}
            />

            {errors.gender && (
              <p className="text-body-regular-xs content-action-negative">{t(errors.gender.message!)}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm text-black dark:text-white leading-5 font-semibold">
              {t("personal.family_status.label")}
              <span className="content-action-negative ml-1">*</span>
            </label>

            <Select
              options={getFamilyStatusOptions(gender, tCommon)}
              value={familyStatus}
              onChange={(v) => setValue("family_status", v ?? "", { shouldValidate: true })}
              placeholder={t("personal.family_status.placeholder")}
            />

            {errors.family_status && (
              <p className="text-body-regular-xs content-action-negative">{t(errors.family_status.message!)}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm text-black dark:text-white leading-5 font-semibold">
              {t("personal.city_of_birth.label")}
              <span className="content-action-negative ml-1">*</span>
            </label>
            <CitySelect
              label=""
              placeholder={t("personal.city_of_birth.select_city_placeholder")}
              searchPlaceholder={t("personal.city_of_birth.select_city_placeholder")}
              value={cityOfBirthId ?? undefined}
              withOther
              otherLabel=""
              otherPlaceholder={t("personal.city_of_birth.other_placeholder")}
              otherValue={cityOfBirthOther ?? ""}
              otherError={errors.city_of_birth_other?.message ? t(errors.city_of_birth_other.message) : undefined}
              dataSource="regions"
              onChange={(cityId) => {
                if (cityId !== undefined) {
                  setValue("city_of_birth_id", cityId, { shouldValidate: submitCount > 0 });
                  setValue("city_of_birth_other", "", { shouldValidate: submitCount > 0 });
                } else {
                  setValue("city_of_birth_id", undefined, { shouldValidate: submitCount > 0 });
                }
              }}
              onOtherChange={(v) => {
                setValue("city_of_birth_other", v, { shouldValidate: submitCount > 0 });
                if (v.trim() !== "") {
                  setValue("city_of_birth_id", undefined, { shouldValidate: submitCount > 0 });
                }
              }}
              error={errors.city_of_birth_id?.message ? t(errors.city_of_birth_id.message) : undefined}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2 w-full">
              <label className="text-sm text-black dark:text-white leading-5 font-semibold">
                {t("personal.nationality.label")}
                <span className="content-action-negative ml-1">*</span>
              </label>
              <Select
                options={getNationalityOptions(gender, t)}
                value={nationality}
                onChange={(v) => setValue("nationality", v ?? "", { shouldValidate: true })}
                placeholder={t("personal.nationality.placeholder")}
              />
              {errors.nationality && (
                <p className="text-body-regular-xs content-action-negative">{t(errors.nationality.message!)}</p>
              )}
            </div>

            <div className="flex flex-col gap-2 w-full">
              <label className="text-sm text-black dark:text-white leading-5 font-semibold">
                {t("personal.citizenship.label")}
                <span className="content-action-negative ml-1">*</span>
              </label>
              <Select
                options={CITIZENSHIP_OPTIONS.map((opt) => ({
                  label: t(`GeneralForm.citizenship.${opt.labelKey}`),
                  value: opt.value,
                }))}
                value={citizenship}
                onChange={(v) => setValue("citizenship", v ?? "", { shouldValidate: true })}
                placeholder={t("personal.citizenship.placeholder")}
              />
              {errors.citizenship && (
                <p className="text-body-regular-xs content-action-negative">{t(errors.citizenship.message!)}</p>
              )}
            </div>
          </div>

          <Checkbox
            state={!isResident ? "checked" : "unchecked"}
            onChange={() => {
              const newValue = !isResident;
              setValue("is_resident", newValue, { shouldValidate: true });
              dispatch(setIsResident(newValue));
            }}
            label={t("personal.not_resident")}
          />

          <Checkbox
            state={isStudentValue ? "checked" : "unchecked"}
            onChange={() => {
              const newValue = !isStudentValue;
              setValue("is_student", newValue, { shouldValidate: true });
              dispatch(setIsStudent(newValue));
              if (!newValue) {
                setValue("enrollment_start_date", null, { shouldValidate: true });
                setValue("enrollment_end_date", null, { shouldValidate: true });
              }
            }}
            label={t("personal.is_student")}
          />

          {isStudentValue && (
            <>
              <FileUploader
                label={t("personal.enrollment.label")}
                value={enrollmentFile}
                existingFileUrl={!isEnrollmentRemoved ? data?.enrollment_verification_file_url : null}
                onChange={(file) => {
                  setValue("enrollment_verification_file", file, { shouldValidate: true });
                  if (file) {
                    setIsEnrollmentRemoved(false);
                  } else {
                    setIsEnrollmentRemoved(false);
                    trigger("enrollment_verification_file");
                  }
                }}
                onRemoveExisting={() => {
                  setIsEnrollmentRemoved(true);
                  setValue("enrollment_verification_file", null, { shouldValidate: true });
                }}
                maxSizeMB={50}
                error={
                  errors.enrollment_verification_file?.message
                    ? t(errors.enrollment_verification_file.message as string)
                    : null
                }
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DatePicker
                  label={t("personal.enrollmentStartDate.label")}
                  hasRequiredLabel
                  placeholder={t("personal.enrollmentStartDate.placeholder")}
                  locale={locale}
                  value={enrollmentStartDate ? new Date(enrollmentStartDate) : null}
                  onChange={(date) => {
                    setValue("enrollment_start_date", date ? (date as Date).toISOString().split("T")[0] : null, { shouldValidate: true });
                  }}
                  error={errors.enrollment_start_date?.message ? t(errors.enrollment_start_date.message as string) : undefined}
                />
                <DatePicker
                  label={t("personal.enrollmentEndDate.label")}
                  hasRequiredLabel
                  placeholder={t("personal.enrollmentEndDate.placeholder")}
                  locale={locale}
                  value={enrollmentEndDate ? new Date(enrollmentEndDate) : null}
                  minDate={enrollmentStartDate ? new Date(enrollmentStartDate) : undefined}
                  onChange={(date) => {
                    setValue("enrollment_end_date", date ? (date as Date).toISOString().split("T")[0] : null, { shouldValidate: true });
                  }}
                  error={errors.enrollment_end_date?.message ? t(errors.enrollment_end_date.message as string) : undefined}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end items-center gap-3">
        {toast && (
          <Toast
            key={`${toast.color}-${toast.text}`}
            color={toast.color}
            text={t(toast.text)}
            onClose={() => setToast(null)}
          />
        )}

        <Button variant="secondary" size="lg" type="button" onClick={handleSave} disabled={isSaving}>
          {isSavingDraft ? t("personal.saving") : t("personal.save")}
        </Button>

        <Button variant="primary" size="lg" type="submit" disabled={isSaving}>
          {isSaving && !isSavingDraft ? t("personal.saving") : t("personal.next_section")}
          {(!isSaving || isSavingDraft) && (
            <span className="text-white">
              <ArrowRightIcon />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
